import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRoutineSetupChecklist } from '../contexts/RoutineSetupChecklistContext';
import { useToast } from '../contexts/ToastContext';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import ProductCard from '../components/ProductCard/ProductCard';
import ResponsiveDialog from '../components/ResponsiveDialog/ResponsiveDialog';
import Carousel from '../components/Carousel';
import LoadingSpinner from '../components/LoadingSpinner';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import { guestDataService } from '../services/guestDataService';
import './SkincareCabinet.css';
import AddProductCard from '../components/ProductCard/AddProductCard';
import EmptyCabinetState from '../components/EmptyCabinetState/EmptyCabinetState';
import { Product } from '../types/routine';

// Extended Product interface for user's cabinet products
// Note: UserProduct extends Product with added_date property

const SkincareCabinet = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [userProducts, setUserProducts] = useState<any[]>([]);
  const [productsInRoutines, setProductsInRoutines] = useState<any[]>([]);
  const [lastRoutineUpdate, setLastRoutineUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdatingRoutines, setIsUpdatingRoutines] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useAuth();
  const { refreshProgress, markUpdateRoutinesSuccess, highlightUpdateRoutinesButton, triggerHighlightUpdateRoutinesButton } = useRoutineSetupChecklist();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const updateRoutinesButtonRef = useRef<HTMLButtonElement>(null);

  // Memoize fetch functions to prevent infinite loops
  const fetchUserProducts = useCallback(async () => {
    try {
      const products = await guestDataService.getProducts(user);
      setUserProducts(products);
    } catch (error) {
      console.error('Error fetching user products:', error);
      setUserProducts([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchProductsInRoutines = useCallback(async () => {
    try {
      const products = await guestDataService.getProductsInRoutines(user);
      setProductsInRoutines(products);
    } catch (error) {
      console.error('Error fetching products in routines:', error);
    }
  }, [user]);

  const fetchLastRoutineUpdate = useCallback(async () => {
    try {
      const lastUpdate = await guestDataService.getLastRoutineUpdate(user);
      setLastRoutineUpdate(lastUpdate);
    } catch (error) {
      console.error('Error fetching last routine update:', error);
    }
  }, [user]);

  useEffect(() => {
    // Always fetch data (works for both guest and authenticated users)
    fetchUserProducts();
    fetchProductsInRoutines();
    fetchLastRoutineUpdate();
  }, [user, fetchUserProducts, fetchProductsInRoutines, fetchLastRoutineUpdate]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Routine setup checklist: open Add Product when navigated with ?openAddProduct=1
  useEffect(() => {
    if (searchParams.get('openAddProduct') === '1') {
      setIsDialogOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openAddProduct');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Routine setup checklist: highlight Update Routines button and scroll into view when ?highlightUpdateRoutines=1
  useEffect(() => {
    if (searchParams.get('highlightUpdateRoutines') === '1') {
      triggerHighlightUpdateRoutinesButton();
      updateRoutinesButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('highlightUpdateRoutines');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, triggerHighlightUpdateRoutinesButton]);



  const categorizeProducts = () => {
    // Normalize to string so Contentful IDs (string) match even if one side was number (legacy/JSON)
    const toId = (id: unknown) => (id == null ? '' : String(id));
    const productsInRoutinesIds = new Set(
      productsInRoutines.map((p) => toId(p.product_id)).filter(Boolean)
    );

    console.log('🔍 CategorizeProducts:', {
      userProductsCount: userProducts.length,
      productsInRoutinesCount: productsInRoutines.length,
      lastRoutineUpdate: lastRoutineUpdate ?? '(never)',
      routineIdSetSize: productsInRoutinesIds.size,
      sampleUserProductIds: userProducts.slice(0, 3).map((p) => ({
        id: p.product_id,
        type: typeof p.product_id,
        normalized: toId(p.product_id),
        inSet: productsInRoutinesIds.has(toId(p.product_id)),
      })),
      sampleRoutineIds: [...productsInRoutinesIds].slice(0, 5),
    });

    // If routines have never been updated, all products go to "Not sorted yet" (since 'Not sorted yet' is now for new products)
    if (!lastRoutineUpdate) {
      return [
        {
          title: "Not sorted yet",
          products: userProducts
        },
        {
          title: "In your routine",
          products: []
        },
        {
          title: "Not in routine",
          products: []
        }
      ];
    }

    const lastUpdateTime = new Date(lastRoutineUpdate);

    // Products added after the last routine update that are not in routines
    const notSortedYet = userProducts.filter((p) => {
      const addedTime = p.added_date ? new Date(p.added_date) : new Date(0);
      const inRoutine = productsInRoutinesIds.has(toId(p.product_id));
      return addedTime >= lastUpdateTime && !inRoutine;
    });

    // Products that are in routines (match by normalized product_id)
    const inRoutineProducts = userProducts.filter((p) =>
      productsInRoutinesIds.has(toId(p.product_id))
    );

    // Products added before the last routine update that are not in routines
    const notInRoutineProducts = userProducts.filter((p) => {
      const addedTime = p.added_date ? new Date(p.added_date) : new Date(0);
      const inRoutine = productsInRoutinesIds.has(toId(p.product_id));
      return addedTime < lastUpdateTime && !inRoutine;
    });

    // Surface why items fall into "Not in routine": no match on product_id
    if (notInRoutineProducts.length > 0 && productsInRoutinesIds.size === 0) {
      console.warn(
        '⚠️ All products in "Not in routine": no products in routines (productsInRoutines empty or unresolved). Check server: in-routines and algorithm use same product_id as cabinet (Contentful entry ID).'
      );
    } else if (notInRoutineProducts.length > 0) {
      console.log('🔍 Not in routine sample (product_id not in routine set):', {
        count: notInRoutineProducts.length,
        sampleIds: notInRoutineProducts.slice(0, 3).map((p) => ({
          product_id: p.product_id,
          type: typeof p.product_id,
          normalized: toId(p.product_id),
          inSet: productsInRoutinesIds.has(toId(p.product_id)),
        })),
      });
    }

    return [
      {
        title: "Not sorted yet",
        products: notSortedYet
      },
      {
        title: "In your routine",
        products: inRoutineProducts
      },
      {
        title: "Not in routine",
        products: notInRoutineProducts
      }
    ];
  };

  const handleAddProduct = async (productId: number) => {
    try {
      const success = await guestDataService.addProduct(user, productId);
      if (success) {
        await fetchUserProducts();
        setIsDialogOpen(false);
        refreshProgress();
      } else {
        console.error('Failed to add product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  const handleAddProductToCabinet = async (productId: number) => {
    try {
      const success = await guestDataService.addProduct(user, productId);
      if (success) {
        await fetchUserProducts();
        refreshProgress();
        showToast('Product added to cabinet');
      } else {
        showToast('Product is already in your cabinet', 'info');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      showToast('Failed to add product', 'error');
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    try {
      const success = await guestDataService.removeProduct(user, productId);
      if (success) {
        // Refresh the products list
        await fetchUserProducts();
        await fetchProductsInRoutines();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const clearSavedRoutineOrderForUser = (userId: string) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    for (const day of days) {
      localStorage.removeItem(`routineOrder_AM_${day}_${userId}`);
      localStorage.removeItem(`routineOrder_PM_${day}_${userId}`);
    }
  };

  const handleUpdateRoutines = async () => {
    setIsUpdatingRoutines(true);
    try {
      if (user?.id) {
        // Authenticated user - use API
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) throw new Error('Failed to update routines');
        
        await response.json();
        await fetchProductsInRoutines();
        await fetchLastRoutineUpdate();
        markUpdateRoutinesSuccess();
        refreshProgress();
        showToast('Routine update complete');
        clearSavedRoutineOrderForUser(String(user.id));

        // Fetch routine explanation (AM + PM) for display on routine pages
        let routineExplanationByRoutineType = null;
        try {
          const explanationRes = await authenticatedFetch(`${API_BASE_URL}/api/routines/explanation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ routineType: 'BOTH' }),
          });
          if (explanationRes.ok) {
            const data = await explanationRes.json();
            if (data.explanationByRoutineType) {
              routineExplanationByRoutineType = data.explanationByRoutineType;
              try {
                sessionStorage.setItem('routineExplanationByRoutineType', JSON.stringify(routineExplanationByRoutineType));
              } catch (_) {
                // ignore storage errors
              }
            }
          }
        } catch (_) {
          // Non-blocking; routine pages will still load
        }

        navigate('/routines/morning', {
          state: routineExplanationByRoutineType ? { routineExplanationByRoutineType } : undefined,
        });
      } else {
        // Guest user - use guest data service
        const success = await guestDataService.updateRoutinesForGuest(user);
        if (success) {
          await fetchProductsInRoutines();
          await fetchLastRoutineUpdate();
          markUpdateRoutinesSuccess();
          refreshProgress();
          showToast('Routine update complete. Your products have been organized into morning and night routines.');
          clearSavedRoutineOrderForUser(user?.username || 'guest');
          navigate('/routines/morning');
        } else {
          throw new Error('Failed to update routines');
        }
      }
    } catch (error) {
      console.error('Error updating routines:', error);
      showToast('Failed to update routines. Please try again.', 'error');
    } finally {
      setIsUpdatingRoutines(false);
    }
  };

  const renderProductSection = (section, sectionIndex) => {
    const productCards = section.products.map((product) => (
      <ProductCard
        key={product.product_id}
        productId={product.product_id}
        company={product.brand}
        productName={product.product_name}
        tag={product.category}
        imageSrc={product.image_url}
        onDelete={() => handleDeleteProduct(product.product_id)}
        product={{
          product_id: product.product_id,
          product_name: product.product_name,
          brand: product.brand,
          category: product.category,
          type: product.type,
          image_url: product.image_url,
          actives: product.actives,
          description: product.description,
          ingredients: product.ingredients,
        }}
        onClick={setSelectedProduct}
      />
    ));

    // Add the AddProductCard for "Not sorted yet" section
    if (section.title === 'Not sorted yet') {
      productCards.push(
        <AddProductCard key="add-product" onClick={() => setIsDialogOpen(true)} />
      );
    }

    if (isMobile) {
      return (
        <div key={sectionIndex} className="product-section">
          <Typography variant="h2" className="section-header">
            {section.title}
          </Typography>
          {section.products.length === 0 && section.title !== 'Not sorted yet' ? (
            <Typography variant="body" className="empty-section">
              No products in this section
            </Typography>
          ) : (
            <Carousel>
              {productCards}
            </Carousel>
          )}
        </div>
      );
    }

    return (
      <div key={sectionIndex} className="product-section">
        <Typography variant="h2" className="section-header">
          {section.title}
        </Typography>
        {section.products.length === 0 && section.title !== 'Not sorted yet' ? (
          <Typography variant="body" className="empty-section">
            No products in this section
          </Typography>
        ) : (
          <div className="product-grid">
            {productCards}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner text="Loading your cabinet..." />;
  }

  const productSections = categorizeProducts();

  return (
    <div className="skincare-cabinet">
        <div className="cabinet-header">
          <Typography variant="h1">Skincare Cabinet</Typography>
          <Button 
            ref={updateRoutinesButtonRef}
            variant="primary" 
            onClick={handleUpdateRoutines}
            disabled={isUpdatingRoutines}
            icon={{
              name: "refresh",
              size: "small",
              className: isUpdatingRoutines ? "spinning" : ""
            }}
            className={highlightUpdateRoutinesButton ? 'routine-setup-checklist-highlight-update-button' : undefined}
          >
              Update Routines
          </Button>
        </div>

      <div className="cabinet-content">
        {userProducts.length === 0 ? (
          <EmptyCabinetState
            title="Your cabinet is empty"
            description="Add your first product to start building personalized morning and night routines."
            actionLabel="Add product"
            onAction={() => setIsDialogOpen(true)}
          />
        ) : (
          <div className="product-sections">
            {productSections.map((section, sectionIndex) => renderProductSection(section, sectionIndex))}
          </div>
        )}
      </div>

      {/* Responsive Product Search Dialog/Bottom Sheet */}
      <ResponsiveDialog
        type="addProduct"
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onAddProduct={handleAddProduct}
      />

      {/* Product detail (from card click) */}
      <ResponsiveDialog
        type="productDetail"
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct ?? undefined}
        onAddProductToCabinet={handleAddProductToCabinet}
      />
    </div>
  );
};

export default SkincareCabinet;
